import React, {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import Modal from "../../components/modal";
// import Modal from "../../components/Modal";

type Modal = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  content?: React.ReactNode;
  hasCloseButton?: boolean;
  hasOverlay?: boolean;
  hasWindowClose?: boolean;
  footer?: React.ReactNode;
  isCustom?: boolean;
  customRef?: any;
};

type Action = {
  type: "open_modal" | "close_modal";
  modal?: Modal;
};

interface State extends Modal {
  isModal: boolean;
}

type Context = {
  state: State;
  openModal: (modal?: Modal) => void;
  closeModal: () => void;
};

type ModalProviderProps = { children: React.ReactNode };

type UseModalProps = {};

interface HTMLEvent extends Event {
  target: HTMLElement;
}

const ModalContext = createContext<Context | undefined>(undefined);

function modalReducer(state: State, action: Action) {
  switch (action.type) {
    case "open_modal": {
      return { ...state, ...action.modal, isModal: true };
    }
    case "close_modal": {
      return { ...state, isModal: false };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

const defaultState = {
  subtitle: undefined,
  title: undefined,
  content: undefined,
  hasCloseButton: true,
  hasOverlay: true,
  footer: undefined,
  isCustom: false,
  customRef: undefined,
  hasWindowClose: true
};

function ModalProvider({ children }: ModalProviderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, dispatch] = useReducer(modalReducer, {
    isModal: false,
    ...defaultState,
  });

  const openModal = (modal?: Modal) => {
    dispatch({ type: "open_modal", modal });
  };

  const closeModal = () => {
    dispatch({ type: "close_modal" });
  };

  useEffect(() => {
    if (state.isModal && state.hasWindowClose) {
      const listener = (e: MouseEvent) => {
        if (
          state.isModal &&
          ref.current &&
          !ref.current.contains(e.target as Node)
        ) {
          closeModal();
        }

        if (
          state.isModal &&
          state.customRef &&
          state.customRef.current &&
          !state.customRef.current.contains(e.target as Node)
        ) {
          closeModal();
        }
      };

      document.addEventListener("mousedown", listener);
      return () => {
        document.removeEventListener("mousedown", listener);
      };
    }
  }, [closeModal]);

  return (
    <ModalContext.Provider
      value={{
        state,
        openModal,
        closeModal,
      }}
    >
      {children}
      {state.isModal && !state.isCustom && (
        <Modal
          modalref={ref}
          title={state.title}
          footer={state.footer}
          handleClose={closeModal}
          subtitle={state.subtitle}
          hasCloseButton={state.hasCloseButton}
          hasOverlay={state.hasOverlay}
        >
          {state.content}
        </Modal>
      )}
    </ModalContext.Provider>
  );
}

function useModal({}: UseModalProps = {}) {
  const context = useContext(ModalContext);

  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }

  return context;
}

export { ModalContext, ModalProvider, useModal };
